/** @format */

import { collection, getDocs, query, where } from "firebase/firestore/lite";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  useAuthContext,
  useGlobalState,
  useSchemaValidation,
} from "senf-shared";
import { db } from "senf-shared/auth/firebase";

import { useIsMobile } from "../../../hooks/customDeviceDetect";
import { cn } from "../../../util/classNameConstructor";
import Button from "../../core/button/Button";
import Icon from "../../core/icon/Icon";
import Form, { type FormProps } from "../../core/inputs/Form";
import { useModals } from "../../molecules/modalStack/ModalProvider";
import useDataPrivacyShield from "../../special/auth/useDataPrivacyShield";

type SimpleAuthOptionsProps = {
  success?: (user: any) => void;
  handleClose?: () => void;
  variant?: "post" | "like" | "comment" | "survey" | "infoPage" | "other";
  project?: {
    settings?: {
      guestLogin?: boolean;
      registration?: boolean;
    };
    projectId?: string;
    title?: string;
    color?: string;
    municipalityName?: string;
  };
};

/**
 * Self-contained simple authentication flow similar to AcceptInvitation:
 * - Shows three options initially
 * - Embeds email login/register forms in-place
 */
const SimpleAuthOptions = ({
  project,
  success,
  variant,
}: SimpleAuthOptionsProps) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const { user } = useAuthContext();
  const { closeModal } = useModals();
  const { handleDataPrivacy } = useDataPrivacyShield(project);
  const validate = useSchemaValidation();
  const { loading, signIn } = useAuthContext();

  // Check project settings
  const guestLoginEnabled = project?.settings?.guestLogin;

  const [view, setView] = useState<
    "options" | "login" | "register" | "emailSent"
  >("options");
  const [sending, setSending] = useState(false);
  const [handleChecking, setHandleChecking] = useState(false);
  const [handleExists, setHandleExists] = useState(false);
  const [translation] = useGlobalState().translationsState;

  const validationSchema = useMemo(() => {
    // For register view, use register schema which includes handle validation
    if (view === "register") {
      return validate.register;
    }
    // For login view, only email validation
    return validate.email;
  }, [validate, view]);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      confirmPassword: "",
      handle: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: () => {},
  });

  // Function to check if handle already exists
  const checkHandleAvailability = useCallback(async (handle: string) => {
    console.log("checkHandleAvailability", handle);
    if (!handle || handle.trim() === "") {
      setHandleExists(false);
      console.log("setHandleExists(false)");
      return;
    }

    console.log("setHandleChecking(true)");
    setHandleChecking(true);
    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("handle", "==", handle.trim()));
      const usernameQuerySnapshot = await getDocs(q);
      console.log(
        "setHandleExists(!usernameQuerySnapshot.empty)",
        !usernameQuerySnapshot.empty,
      );
      setHandleExists(!usernameQuerySnapshot.empty);
    } catch (error) {
      console.log("setHandleExists(false)");
      console.error("Error checking handle availability:", error);
      setHandleExists(false);
    } finally {
      console.log("setHandleChecking(false)");
      setHandleChecking(false);
    }
  }, []);

  // Debounced handle checking
  useEffect(() => {
    console.log("view", view, formik.values.handle);
    const timeoutId = setTimeout(() => {
      if (view === "register" && formik.values.handle) {
        checkHandleAvailability(formik.values.handle);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [formik.values.handle, view, checkHandleAvailability]);

  const inputItemsLogin: FormProps["inputItems"] = [
    {
      name: "email",
      type: "email",
      placeholder: t("auth:email"),
      leadingIcon: "mail",
    },
  ];

  const inputItemsRegister: FormProps["inputItems"] = [
    {
      name: "email",
      type: "email",
      placeholder: t("auth:email"),
      leadingIcon: "mail",
    },
    {
      name: "handle",
      type: "text",
      placeholder: `${t("auth:publicUsername")} (${t("global:optional")})`,
      leadingIcon: "user",
      success:
        formik.values.handle?.length >= 3 && !handleExists
          ? t("auth:usernameAvailable")
          : undefined,
      error: handleExists ? t("auth:error.usernameAlreadyExists") : undefined,
    },
  ];

  // use current URL as redirect for magic link handling
  const redirectUrl =
    typeof window !== "undefined" ? window.location.href : undefined;

  // For registration, include handle in URL parameters for cross-browser compatibility
  const getRedirectUrlWithHandle = () => {
    if (view === "register" && formik.values.handle && redirectUrl) {
      const url = new URL(redirectUrl);
      url.searchParams.set("handle", formik.values.handle);
      return url.toString();
    }
    return redirectUrl;
  };

  const handleEmailSubmit = async () => {
    const privacyAccepted = await handleDataPrivacy();
    if (!privacyAccepted) return;
    const email = formik?.values?.email?.trim();
    if (!email) return;
    try {
      setSending(true);
      await fetch(
        `${import.meta.env.VITE_FIREBASE_CLOUD_FUNCTIONS_URL}/onSendMagicLink`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            redirectUrl: getRedirectUrlWithHandle(),
            // Prefer localized title if available in global translations
            projectTitle:
              (translation as Record<string, string> | undefined)?.[
                (project as { title?: string } | undefined)?.title || ""
              ] || (project as { title?: string } | undefined)?.title,
            brandColor: (project as { color?: string } | undefined)?.color,
            lang:
              (
                i18n as unknown as {
                  resolvedLanguage?: string;
                  language?: string;
                }
              ).resolvedLanguage ||
              (
                i18n as unknown as {
                  resolvedLanguage?: string;
                  language?: string;
                }
              ).language,
            municipalityName: (
              project as
                | { location?: { municipalityName?: string } }
                | undefined
            )?.location?.municipalityName,
          }),
        },
      );
      console.log("email sent", project);

      setView("emailSent");
    } finally {
      setSending(false);
    }
  };

  // Auto-close modal when user becomes authenticated
  // useEffect(() => {
  //   if (user?.userId) {
  //     if (success) {
  //       success(user);
  //     } else {
  //       closeModal();
  //     }
  //   }
  // }, [user, success, closeModal]);

  if (view !== "options") {
    const isRegister = view === "register";
    if (view === "emailSent") {
      return (
        <div className="px-4 py-6 flex flex-col items-center">
          <Icon
            name="paperplane"
            className="mx-auto mb-6 h-10 w-10 text-icon-default"
          />
          <h2 className="heading-xl font-black mb-2">
            {t("auth:emailSentTitle")}
          </h2>
          <p className="body-md text-text-subdued mb-6 text-center">
            {t("auth:emailSentDescription")}
          </p>
          <Button
            variant="primary"
            size="xl"
            width="max"
            text={t("util:open", { val: t("auth:mailbox") })}
            classNameWrapper="w-full"
            onClick={() => {
              window.location.href = "mailto:";
            }}
          />
        </div>
      );
    }

    return (
      <div
        className={cn(
          "flex flex-col gap-6 px-6 pt-5 pb-8",
          isMobile ? "pt-8" : "pt-8",
        )}
      >
        <h1
          className={cn(
            "font-black",
            isMobile ? "heading-xl" : "heading-3xl text-center",
          )}
        >
          {t(isRegister ? "auth:register" : "auth:login")}
        </h1>
        <Form
          inputItems={isRegister ? inputItemsRegister : inputItemsLogin}
          formik={formik}
        />

        <span className="body-sm text-text-subdued">
          <Trans i18nKey="auth:terms">
            ...
            <Link
              to={`/projects/${project?.projectId}/terms`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "underline", fontWeight: "500" }}
            />
            ...
            <Link
              to={`/projects/${project?.projectId}/privacyPolicy`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "underline", fontWeight: "500" }}
            />
            ...
          </Trans>
        </span>

        <div className="flex items-center gap-2">
          <Button
            leadingIcon="arrowhead-left-md"
            size="lg"
            onClick={() => setView("options")}
          />

          <Button
            variant="primary"
            width="max"
            size="lg"
            text={
              sending
                ? t("auth:sendingMagicLink", "Sende Link...")
                : t(isRegister ? "auth:register" : "auth:login")
            }
            loading={sending}
            onClick={handleEmailSubmit}
            disabled={
              !formik?.values?.email?.trim() ||
              !!formik?.errors?.email ||
              !formik?.isValid ||
              sending ||
              handleExists
            }
            data-cy="email-login-register-submit"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 items-stretch px-6 py-16">
      <h1 className="text-center heading-3xl font-black pb-3">
        {variant === "post"
          ? t("util:create", { val: t("global:post") })
          : variant === "like"
            ? t("util:like", { val: t("global:post") })
            : variant === "comment"
              ? t("util:comment", { val: t("global:post") })
              : variant === "survey"
                ? t("util:start", { val: t("global:survey") })
                : t("global:participateNow")}
      </h1>

      {guestLoginEnabled ? (
        <>
          {/* 1) Guest login primary */}
          <Button
            variant="primary"
            width="max"
            size="lg"
            pillShaped
            text={t("auth:authOptions.anonymousLogin")}
            loading={loading === "anonymous"}
            onClick={async () => {
              const privacyAccepted = await handleDataPrivacy();
              if (privacyAccepted) {
                const formikStoreWithProject = {
                  ...formik,
                  values: {
                    ...formik.values,
                    projectId: project?.projectId,
                    referrer: document.referrer || "",
                    utm_id:
                      new URLSearchParams(window.location.search).get(
                        "utm_id",
                      ) || "",
                  },
                };
                const updatedUser = await signIn?.({
                  provider: "anonymous",
                  formikStore: formikStoreWithProject,
                });
                if (updatedUser) {
                  success?.(updatedUser);
                }
              }
            }}
          />

          {/* 2) Register outline */}
          <Button
            variant="outline"
            width="max"
            size="lg"
            pillShaped
            text={t("auth:register")}
            onClick={() => setView("register")}
          />

          {/* 3) Login shapeless */}
          <Button
            variant="shapeless"
            width="max"
            size="lg"
            text={t("auth:login")}
            textSubdued
            onClick={() => setView("login")}
          />
        </>
      ) : (
        <>
          {/* Without guest login: Register primary, Login outline */}
          <Button
            variant="primary"
            width="max"
            size="lg"
            pillShaped
            text={t("auth:register")}
            onClick={() => setView("register")}
          />

          <Button
            variant="outline"
            width="max"
            size="lg"
            pillShaped
            text={t("auth:login")}
            onClick={() => setView("login")}
          />
        </>
      )}
    </div>
  );
};

export default SimpleAuthOptions;
